<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getMacros, patchMacro, deleteMacro, getMacroRuns } from '@/api/api.js'
import { useToast } from '@/composables/useToast.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import MdiPlus from '~icons/mdi/plus'
import MdiPencil from '~icons/mdi/pencil'
import MdiDelete from '~icons/mdi/delete'
import MdiRobot from '~icons/mdi/robot'

const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const macros = ref([])
const loading = ref(false)
const loadError = ref(null)
const togglingId = ref(null)
const confirmDelete = ref(null)
const deleting = ref(false)
const macroRuns = ref([])

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const [res, runsRes] = await Promise.all([
      getMacros({ page_size: 200 }),
      getMacroRuns({ page_size: 20 }),
    ])
    macros.value = res.results ?? res ?? []
    macroRuns.value = runsRes.results ?? runsRes ?? []
  } catch (e) {
    loadError.value = e?.data?.detail ?? t('macros.failedLoad')
  } finally {
    loading.value = false
  }
}

function runSkippedCount(run) {
  let count = 0

  const topLevel = run?.result?.skipped_items
  if (Array.isArray(topLevel)) count += topLevel.length

  const steps = Array.isArray(run?.step_runs) ? run.step_runs : []
  for (const step of steps) {
    const skipped = step?.result?.skipped_items
    if (Array.isArray(skipped)) count += skipped.length
  }

  return count
}

function hasRunWarnings(run) {
  return run?.status === 'success' && runSkippedCount(run) > 0
}

function runBadgeClass(run) {
  if (hasRunWarnings(run)) return 'badge-warning'
  if (run?.status === 'success') return 'badge-success'
  if (run?.status === 'failed' || run?.status === 'rolled_back') return 'badge-danger'
  return 'badge-muted'
}

function runStatusLabel(run) {
  if (hasRunWarnings(run)) return t('macros.successWithWarnings')
  return run?.status ?? t('macros.unknownStatus')
}

function formatDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleString()
}

async function toggleActive(macro) {
  togglingId.value = macro.id
  try {
    const updated = await patchMacro(macro.id, { is_active: !macro.is_active })
    const idx = macros.value.findIndex((m) => m.id === macro.id)
    if (idx !== -1) macros.value[idx] = { ...macros.value[idx], ...updated }
    toast.success(updated.is_active ? t('macros.activatedToast') : t('macros.deactivatedToast'))
  } catch {
    toast.error(t('macros.failedUpdate'))
  } finally {
    togglingId.value = null
  }
}

async function doDelete() {
  if (!confirmDelete.value) return
  deleting.value = true
  try {
    await deleteMacro(confirmDelete.value.id)
    macros.value = macros.value.filter((m) => m.id !== confirmDelete.value.id)
    toast.success(t('macros.deletedToast'))
    confirmDelete.value = null
  } catch {
    toast.error(t('macros.failedDelete'))
  } finally {
    deleting.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="$t('macros.title')" @back="router.back()">
      <template #actions>
        <button class="btn primary small" @click="router.push('/admin/macros/new')">
          <MdiPlus class="w-4 h-4" />
          {{ $t('macros.new') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Loading -->
    <div v-if="loading" class="list-stack">
      <div v-for="i in 3" :key="i" class="data-card animate-pulse">
        <div class="data-card__main">
          <div class="h-4 w-1/3 bg-white/10 rounded mb-2"></div>
          <div class="h-3 w-1/2 bg-white/5 rounded"></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <template v-else-if="loadError">
      <EmptyState :message="loadError" variant="error" />
      <div class="flex justify-center mt-2">
        <button class="btn ghost small" @click="load">{{ $t('macros.tryAgain') }}</button>
      </div>
    </template>

    <!-- Empty -->
    <template v-else-if="!macros.length">
      <div class="state-empty flex flex-col items-center gap-3 py-16">
        <MdiRobot class="w-10 h-10 text-[var(--color-muted)]" />
        <p class="text-[var(--color-muted)] m-0">{{ $t('macros.noMacrosYet') }}</p>
        <button class="btn primary small" @click="router.push('/admin/macros/new')">
          {{ $t('macros.createFirst') }}
        </button>
      </div>
    </template>

    <!-- List -->
    <div v-else class="list-stack">
      <div
        v-for="macro in macros"
        :key="macro.id"
        :class="['data-card cursor-pointer transition-opacity', !macro.is_active && 'opacity-50']"
        @click="router.push(`/admin/macros/${macro.id}`)"
      >
        <div class="data-card__main">
          <div class="data-card__name flex items-center gap-2">
            <MdiRobot class="w-4 h-4 text-[var(--color-primary)] flex-shrink-0" />
            {{ macro.name }}
          </div>
          <div class="data-card__meta">
            <span v-if="macro.description" class="text-[var(--color-muted)] text-sm truncate max-w-xs">{{ macro.description }}</span>
            <span class="badge badge-muted">{{ $t('macros.stepsCount', { n: macro.steps?.length ?? 0 }) }}</span>
            <span :class="['badge', macro.is_active ? 'badge-success' : 'badge-muted']">{{ macro.is_active ? $t('macros.activeStatus') : $t('macros.inactiveStatus') }}</span>
          </div>
        </div>
        <div class="data-card__actions" @click.stop>
          <!-- Active toggle -->
          <button
            :class="[
              'relative w-10 h-5 rounded-full transition-colors flex-shrink-0',
              macro.is_active ? 'bg-[var(--color-primary)]' : 'bg-gray-600',
              togglingId === macro.id && 'opacity-50 pointer-events-none'
            ]"
            :title="macro.is_active ? $t('macros.deactivateTitle') : $t('macros.activateTitle')"
            @click="toggleActive(macro)"
          >
            <span
              :class="[
                'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
                macro.is_active ? 'left-[calc(100%-18px)]' : 'left-0.5'
              ]"
            />
          </button>
          <button class="btn-icon" :title="$t('macros.editTitle')" @click="router.push(`/admin/macros/${macro.id}`)">
            <MdiPencil class="w-4 h-4" />
          </button>
          <button class="btn-icon danger" :title="$t('macros.deleteTitle')" @click="confirmDelete = macro">
            <MdiDelete class="w-4 h-4" />
          </button>
        </div>
      </div>

      <div class="section-label mt-3">{{ $t('macros.recentRuns') }}</div>
      <div v-if="!macroRuns.length" class="state-empty">{{ $t('macros.noRuns') }}</div>
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="run in macroRuns"
          :key="run.id"
          class="data-card"
        >
          <div class="data-card__main">
            <div class="data-card__name">
              {{ run.macro_name || t('ui.macroNumber', { id: run.macro }) }}
            </div>
            <div class="data-card__meta">
              <span class="text-[var(--color-muted)] text-xs">{{ $t('macros.runPrefix', { id: run.id }) }}</span>
              <span class="text-[var(--color-muted)] text-xs">{{ formatDateTime(run.created_at) }}</span>
              <span v-if="hasRunWarnings(run)" class="text-amber-300 text-xs">
                {{ runSkippedCount(run) }} {{ runSkippedCount(run) === 1 ? $t('macros.skippedItem') : $t('macros.skippedItems') }}
              </span>
            </div>
          </div>
          <div class="data-card__actions">
            <span :class="['badge', runBadgeClass(run)]">{{ runStatusLabel(run) }}</span>
          </div>
        </div>
      </div>
    </div>

    <BaseConfirmModal
      :show="!!confirmDelete"
      :title="$t('macros.deleteConfirmTitle')"
      :body="confirmDelete ? $t('macros.deleteConfirmBody', { name: confirmDelete?.name }) : ''"
      :confirm-label="$t('macros.deleteConfirmLabel')"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="confirmDelete = null"
    />
  </div>
</template>
